import { useSearchParams } from 'react-router-dom';
import { getJson } from '@/lib/api';
import type { StatsInterval } from '@/lib/leads';

/**
 * Page-type analytics — typed fetcher over /api/v3/pages/types. Rolls the
 * site's traffic up by page template (program, category, blog-post, …) with
 * sessions, on-page leads and CVR over the window plus per-bucket series.
 * Shapes mirror apps/api's PageTypesStats.
 */

/** One page type over the window: totals, CVR, deltas and per-bucket series. */
export interface PageTypeRow {
  readonly template: string;
  /** Distinct pages of this type with traffic in the window. */
  readonly pages: number;
  readonly sessions: number;
  /** On-page leads (form submitted on a page of this type) in the window. */
  readonly leads: number;
  /** leads / sessions (a 0–1 fraction), null when the type had no sessions. */
  readonly cvr: number | null;
  readonly clicks: number;
  /** Sessions in the same-length window before `from`. */
  readonly previous: number;
  /** Sessions vs previous sessions, a signed percent (null when no baseline). */
  readonly changePct: number | null;
  /** Search impressions in the window. */
  readonly impressions: number;
  readonly previousImpressions: number;
  /** Impressions vs the previous window, a signed percent (null when no baseline). */
  readonly impressionsChangePct: number | null;
  /** Impressions-weighted mean search position; null when Search Console never showed the type. */
  readonly avgPosition: number | null;
  readonly previousAvgPosition: number | null;
  /** Positions gained since the previous window (positive = moved up); null without both. */
  readonly positionChange: number | null;
  /** Sessions per bucket, aligned to `buckets`. */
  readonly sessionsSeries: readonly number[];
  /** On-page leads per bucket, aligned to `buckets`. */
  readonly leadsSeries: readonly number[];
  /** Search clicks per bucket, aligned to `buckets`. */
  readonly clicksSeries: readonly number[];
  /** Search impressions per bucket, aligned to `buckets`. */
  readonly impressionsSeries: readonly number[];
  /** Impressions-weighted position per bucket; null where Google showed none. */
  readonly positionSeries: readonly (number | null)[];
}

/**
 * Which traffic the CVR covers. Paid = Google Ads only: sessions GA4
 * attributes to google / cpc over leads with a Google Ads click id. Mirrors
 * apps/api's PAGE_TRAFFIC.
 */
export const PAGE_TRAFFIC = {
  all: 'all',
  paid: 'paid',
} as const;
export type PageTraffic = (typeof PAGE_TRAFFIC)[keyof typeof PAGE_TRAFFIC];

export const PAGE_TRAFFIC_LABELS: Readonly<Record<PageTraffic, string>> = {
  [PAGE_TRAFFIC.all]: 'All traffic',
  [PAGE_TRAFFIC.paid]: 'Paid',
};

/** The URL param the traffic toggle lives in (`?traffic=paid`; absent = all). */
export const TRAFFIC_PARAM = 'traffic';

export interface PageTypesStats {
  readonly range: { readonly from: string; readonly to: string; readonly interval: StatsInterval };
  /** The bucket keys the per-type series align to. */
  readonly buckets: readonly string[];
  /** One entry per page type, most sessions first. */
  readonly types: readonly PageTypeRow[];
  readonly traffic: PageTraffic;
  /**
   * Paid mode: the first day in the range with paid sessions synced
   * (YYYY-MM-DD), or null when none has been synced for the range yet.
   * Always null for all traffic.
   */
  readonly paidDataFrom: string | null;
}

/** Friendly labels for the raw template keys the classifier writes. */
const TEMPLATE_LABELS: Readonly<Record<string, string>> = {
  homepage: 'Homepage',
  program: 'Programs',
  'program-category': 'Program categories',
  location: 'Locations',
  'blog-post': 'Blog posts',
  'blog-index': 'Blog index',
  'static-page': 'Static pages',
  ebook: 'Ebooks',
  form: 'Forms',
  'landing-page': 'Landing pages',
};

/** A human label for a template key ("Program categories"), falling back to the raw key. */
export function templateLabel(template: string): string {
  if (template === '') return 'Unclassified';
  return TEMPLATE_LABELS[template] ?? template;
}

export function readPageTypes(
  params: {
    from: string;
    to: string;
    interval: StatsInterval;
    leadIn?: number;
    includeHidden?: boolean;
    traffic?: PageTraffic;
  },
  signal?: AbortSignal,
): Promise<PageTypesStats> {
  const { includeHidden, traffic, ...rest } = params;
  return getJson<PageTypesStats>(
    '/pages/types',
    {
      ...rest,
      ...(includeHidden ? { includeHidden: '1' } : {}),
      ...(traffic === PAGE_TRAFFIC.paid ? { traffic } : {}),
    },
    signal,
  );
}

/** A URL value → traffic mode; anything unknown reads as all traffic. */
export function parseTraffic(value: string | null): PageTraffic {
  return value === PAGE_TRAFFIC.paid ? PAGE_TRAFFIC.paid : PAGE_TRAFFIC.all;
}

/** `?traffic=paid` for paid, '' for all: appended to links so the drill-down keeps the mode. */
export function trafficSearch(traffic: PageTraffic): string {
  return traffic === PAGE_TRAFFIC.paid ? `?${TRAFFIC_PARAM}=${PAGE_TRAFFIC.paid}` : '';
}

/**
 * URL-backed traffic toggle (`?traffic=paid`), like the table sort, so a
 * paid view survives reload, can be shared, and carries into the type
 * drill-down. All traffic is the default and keeps the URL clean.
 */
export function useTrafficParam(): { readonly traffic: PageTraffic; readonly setTraffic: (next: PageTraffic) => void } {
  const [params, setParams] = useSearchParams();
  const traffic = parseTraffic(params.get(TRAFFIC_PARAM));
  function setTraffic(next: PageTraffic): void {
    setParams(
      (previous) => {
        const updated = new URLSearchParams(previous);
        if (next === PAGE_TRAFFIC.paid) updated.set(TRAFFIC_PARAM, next);
        else updated.delete(TRAFFIC_PARAM);
        updated.delete('page');
        return updated;
      },
      { replace: true },
    );
  }
  return { traffic, setTraffic };
}

/** "All traffic | Paid" options for the SegmentedControl. */
export const TRAFFIC_OPTIONS = (Object.keys(PAGE_TRAFFIC_LABELS) as PageTraffic[]).map((key) => ({
  key,
  label: PAGE_TRAFFIC_LABELS[key],
}));
